/*
 * Size Finder — client-side logic.
 *
 * Reads the product catalog from an embedded <script type="application/json">
 * blob that the Liquid section renders. Classifies each product (gender,
 * category, occasions, best-seller, gift score) from Shopify tags, product
 * type, and a fallback handle-based map. Then runs the same two-flow UI
 * the demo used (Shopping vs Gifting).
 *
 * Tag conventions the merchandiser can use in Shopify admin to control
 * the tool without touching code:
 *
 *   occasion:wedding | occasion:party | occasion:birthday | occasion:everyday
 *   gender:girl      | gender:boy     | gender:unisex
 *   bestseller
 *   gift-pick
 *
 * If a product has no relevant tags, the code falls back to heuristics
 * based on product type and handle so the tool still works.
 */

(function () {
  'use strict';

  // ------------------------------------------------------------
  // Size chart (from momandzoey.com/pages/garment-sizing-reference)
  // ------------------------------------------------------------
  const CHART = {
    'girls-top': [
      { size: '2-3Y', hMin: 86,  hMax: 96,  wMin: 11,   wMax: 14   },
      { size: '3-4Y', hMin: 96,  hMax: 104, wMin: 13.5, wMax: 16.5 },
      { size: '4-5Y', hMin: 104, hMax: 113, wMin: 15.5, wMax: 18.5 },
      { size: '5-6Y', hMin: 113, hMax: 121, wMin: 17.5, wMax: 21   }
    ],
    'boys-top': [
      { size: '2-3Y', hMin: 87,  hMax: 96,  wMin: 12,   wMax: 14   },
      { size: '3-4Y', hMin: 96,  hMax: 105, wMin: 13.5, wMax: 16.5 },
      { size: '4-5Y', hMin: 105, hMax: 114, wMin: 15.5, wMax: 18   },
      { size: '5-6Y', hMin: 114, hMax: 122, wMin: 17.5, wMax: 21   }
    ],
    'dress': [
      { size: '2-3Y', hMin: 86,  hMax: 96,  wMin: 11,   wMax: 14   },
      { size: '3-4Y', hMin: 96,  hMax: 104, wMin: 13.5, wMax: 16.5 },
      { size: '4-5Y', hMin: 104, hMax: 113, wMin: 15.5, wMax: 18.5 },
      { size: '5-6Y', hMin: 113, hMax: 121, wMin: 17.5, wMax: 21   }
    ],
    'bottom': [
      { size: '2-3Y', hMin: 86,  hMax: 96,  wMin: 11,   wMax: 14   },
      { size: '3-4Y', hMin: 96,  hMax: 105, wMin: 13.5, wMax: 16.5 },
      { size: '4-5Y', hMin: 105, hMax: 114, wMin: 15.5, wMax: 18.5 },
      { size: '5-6Y', hMin: 114, hMax: 122, wMin: 17.5, wMax: 21   }
    ]
  };

  const SIZE_ORDER = ['2-3Y', '3-4Y', '4-5Y', '5-6Y'];

  // ------------------------------------------------------------
  // Handle-based fallbacks — used when product tags don't specify
  // gender/category/occasions. Keep in sync with any known SKUs.
  // ------------------------------------------------------------
  const HANDLE_FALLBACKS = {
    // Girls dresses
    'santorini-organic-cotton-flutter-sleeve-dress':     { gender: 'girl', category: 'dress', occasions: ['party', 'birthday'], bestSeller: true, giftScore: 9 },
    'riviera-rose-organic-cotton-linen-floral-dress':    { gender: 'girl', category: 'dress', occasions: ['wedding', 'party'],  bestSeller: true, giftScore: 10 },
    'mykonos-dress-organic-cotton-embroidered-smocked-dress': { gender: 'girl', category: 'dress', occasions: ['birthday', 'party'], giftScore: 8 },
    'provence-organic-cotton-linen-dress':               { gender: 'girl', category: 'dress', occasions: ['wedding', 'party'],  giftScore: 8 },
    'portofino-dress-organic-cotton-striped':            { gender: 'girl', category: 'dress', occasions: ['everyday', 'birthday'], giftScore: 7 },
    // Girls tops
    'capri-organic-cotton-girls-blouse-white':           { gender: 'girl', category: 'top', occasions: ['everyday', 'party'], bestSeller: true, giftScore: 7 },
    'cairo-organic-cotton-linen-girls-blouse':           { gender: 'girl', category: 'top', occasions: ['party'], giftScore: 7 },
    'vienna-organic-cotton-girls-blouse-white':          { gender: 'girl', category: 'top', occasions: ['everyday'], giftScore: 6 },
    'osaka-organic-cotton-linen-blazer-blouse-lilac':    { gender: 'girl', category: 'top', occasions: ['wedding', 'party'], giftScore: 8 },
    // Girls bottoms
    'amalfi-girls-skirt':                                { gender: 'girl', category: 'bottom', occasions: ['everyday', 'birthday'], giftScore: 6 },
    // Girls sets
    'capri-blouse-amalfi-skort-set':                     { gender: 'girl', category: 'set', occasions: ['wedding', 'party'], giftScore: 10 },
    'capri-blouse-and-nice-shorts':                      { gender: 'girl', category: 'set', occasions: ['everyday', 'birthday'], giftScore: 8 },
    // Boys tops
    'copenhagen-organic-cotton-kids-striped-shirt':      { gender: 'boy', category: 'top', occasions: ['everyday', 'birthday'], bestSeller: true, giftScore: 8 },
    'cannes-organic-cotton-kids-shirt':                  { gender: 'boy', category: 'top', occasions: ['everyday', 'birthday'], bestSeller: true, giftScore: 8 },
    'milan-organic-cotton-linen-kids-shirt-lilac':       { gender: 'boy', category: 'top', occasions: ['wedding', 'party'], giftScore: 9 },
    'petra-organic-cotton-linen-kids-shirt':             { gender: 'unisex', category: 'top', occasions: ['wedding'], giftScore: 8 },
    'oslo-organic-cotton-linen-kids-shirt':              { gender: 'boy', category: 'top', occasions: ['party', 'everyday'], giftScore: 7 },
    // Unisex bottoms (merchant tags them with both boys AND girls AND unisex)
    'positano-kids-pants':                               { gender: 'unisex', category: 'bottom', occasions: ['wedding', 'party'], giftScore: 7 },
    'nice-kids-shorts':                                  { gender: 'unisex', category: 'bottom', occasions: ['everyday'], giftScore: 6 },
    'palermo-kids-shorts':                               { gender: 'unisex', category: 'bottom', occasions: ['everyday', 'birthday'], giftScore: 6 },
    // Unisex sets
    'florence-lounge-coord-set-organic-cotton-linen':    { gender: 'unisex', category: 'set', occasions: ['everyday', 'party'], giftScore: 9 }
  };

  // ------------------------------------------------------------
  // Init: one instance per .size-finder element on the page
  // ------------------------------------------------------------
  function init() {
    const roots = document.querySelectorAll('[data-size-finder]');
    for (let i = 0; i < roots.length; i++) {
      initInstance(roots[i]);
    }
  }

  function initInstance(root) {
    const catalogEl = root.querySelector('[data-size-finder-catalog]');
    if (!catalogEl) {
      console.warn('[size-finder] no catalog data found');
      return;
    }

    let catalog;
    try {
      catalog = JSON.parse(catalogEl.textContent);
    } catch (err) {
      console.error('[size-finder] catalog parse error', err);
      return;
    }

    const products = (catalog.products || []).map(classify).filter(function (p) {
      return p.category && p.category !== 'other';
    });

    const defaultMode = root.getAttribute('data-default-mode') || 'shopping';
    const defaultGender = root.getAttribute('data-default-gender') || 'girl';
    const defaultOccasion = root.getAttribute('data-default-occasion') || 'any';

    const state = {
      mode: defaultMode,
      gender: defaultGender,
      category: 'any',
      shop_occasion: defaultOccasion,
      height: 98,
      weight: 15,
      age: '3-4Y',
      gift_gender: defaultGender,
      gift_age: '3-4Y',
      gift_build: 'average',
      gift_occasion: defaultOccasion
    };

    wireInputs(root, state, products);
    render(root, state, products);
  }

  // ------------------------------------------------------------
  // Product classification
  // ------------------------------------------------------------
  function classify(p) {
    const handle = p.handle || '';
    const type = (p.type || '').toLowerCase();
    const tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
    const fallback = HANDLE_FALLBACKS[handle] || {};

    const gender = readTag(tags, 'gender') || detectGender(handle, type, tags) || fallback.gender || 'unisex';
    const category = readTag(tags, 'category') || detectCategory(type, handle) || fallback.category || 'other';
    const occasions = readOccasions(tags).length ? readOccasions(tags) : (fallback.occasions || []);
    const bestSeller = tags.indexOf('bestseller') >= 0 || tags.indexOf('best-seller') >= 0 || fallback.bestSeller === true;
    const giftPick = tags.indexOf('gift-pick') >= 0;
    const giftScore = giftPick ? 10 : (fallback.giftScore != null ? fallback.giftScore : 6);
    const availableSizes = detectAvailableSizes(p.variants || []);

    return {
      id: p.id,
      handle: handle,
      title: cleanTitle(p.title),
      subtitle: cleanSubtitle(p.title, type),
      price: typeof p.price === 'number' ? p.price : 0,
      url: p.url || ('/products/' + handle),
      image: p.image || '',
      gender: gender,
      category: category,
      occasions: occasions,
      bestSeller: bestSeller,
      giftScore: giftScore,
      availableSizes: availableSizes,
      variants: p.variants || []
    };
  }

  function readTag(tags, prefix) {
    const needle = prefix + ':';
    for (let i = 0; i < tags.length; i++) {
      if (tags[i].indexOf(needle) === 0) return tags[i].slice(needle.length);
    }
    return null;
  }

  function readOccasions(tags) {
    const out = [];
    for (let i = 0; i < tags.length; i++) {
      if (tags[i].indexOf('occasion:') === 0) {
        out.push(tags[i].slice('occasion:'.length));
      }
    }
    return out;
  }

  // Detect gender from tags (preferred) or handle. Mom & Zoey's convention is
  // to tag unisex products with BOTH `boys` AND `girls` AND `unisex`, so we
  // must check for the dual-tagged case before returning either gender.
  function detectGender(handle, type, tags) {
    // Explicit gender: tag wins
    for (let i = 0; i < tags.length; i++) {
      const t = tags[i];
      if (t === 'gender:girl') return 'girl';
      if (t === 'gender:boy') return 'boy';
      if (t === 'gender:unisex') return 'unisex';
    }
    // Explicit `unisex` tag
    if (tags.indexOf('unisex') >= 0) return 'unisex';
    // Word-boundary match for boys/girls inside any tag
    let hasGirls = false;
    let hasBoys = false;
    for (let i = 0; i < tags.length; i++) {
      if (/\bgirls?\b/.test(tags[i])) hasGirls = true;
      if (/\bboys?\b/.test(tags[i])) hasBoys = true;
    }
    if (hasGirls && hasBoys) return 'unisex';
    if (hasGirls) return 'girl';
    if (hasBoys) return 'boy';
    // Handle-based fallback
    if (/\bgirls?\b/.test(handle)) return 'girl';
    if (/\bboys?\b/.test(handle)) return 'boy';
    // Dress/skirt is a safe girl default; drop the shirt/pants->boy heuristic
    if (/dress|frock|skirt|skort|gown/.test(handle + ' ' + type)) return 'girl';
    return null;
  }

  // Detect category — order matters. `set` must be checked before `top` so
  // "capri-blouse-and-nice-shorts" (type Set) doesn't match the `blouse` branch.
  function detectCategory(type, handle) {
    const t = type + ' ' + handle;
    if (/\bset\b|coord|co-ord|bundle/.test(t)) return 'set';
    if (/dress|frock|gown/.test(t)) return 'dress';
    if (/shirt|blouse|top|tee|tshirt|t-shirt/.test(t)) return 'top';
    if (/pants|shorts|skirt|skort|trouser/.test(t)) return 'bottom';
    return null;
  }

  function detectAvailableSizes(variants) {
    const seen = {};
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      if (!v.available) continue;
      const opt = (v.option1 || v.title || '').toUpperCase().replace(/\s/g, '');
      const normalized = normalizeSize(opt);
      if (normalized) seen[normalized] = true;
    }
    const out = [];
    for (let i = 0; i < SIZE_ORDER.length; i++) {
      if (seen[SIZE_ORDER[i]]) out.push(SIZE_ORDER[i]);
    }
    // If we couldn't match any sizes from variants, assume all sizes are available
    return out.length ? out : SIZE_ORDER.slice();
  }

  function normalizeSize(raw) {
    const s = String(raw).toUpperCase();
    if (/2[-_\s]?3Y?/.test(s)) return '2-3Y';
    if (/3[-_\s]?4Y?/.test(s)) return '3-4Y';
    if (/4[-_\s]?5Y?/.test(s)) return '4-5Y';
    if (/5[-_\s]?6Y?/.test(s)) return '5-6Y';
    return null;
  }

  function cleanTitle(raw) {
    if (!raw) return '';
    // Most Mom & Zoey titles are like "Santorini Dress | Organic Cotton ..."
    return raw.split('|')[0].trim();
  }

  function cleanSubtitle(raw, type) {
    if (!raw) return type || '';
    const parts = raw.split('|');
    if (parts.length > 1) return parts.slice(1).join('|').trim();
    return type || '';
  }

  // ------------------------------------------------------------
  // Size calculation
  // ------------------------------------------------------------
  function chartKey(gender, category) {
    if (category === 'dress') return 'dress';
    if (category === 'bottom') return 'bottom';
    if (category === 'set') return gender === 'girl' ? 'dress' : 'boys-top';
    return gender === 'boy' ? 'boys-top' : 'girls-top';
  }

  function findSizeByMeasurements(gender, category, heightCm, weightKg) {
    const chart = CHART[chartKey(gender, category)];
    const hits = chart.filter(function (r) { return heightCm >= r.hMin && heightCm <= r.hMax; });

    if (hits.length === 1) {
      const row = hits[0];
      const pos = (heightCm - row.hMin) / Math.max(1, row.hMax - row.hMin);
      return { size: row.size, position: pos, borderline: false };
    }
    if (hits.length === 2) {
      const weightHit = hits.filter(function (r) { return weightKg >= r.wMin && weightKg <= r.wMax; })[0];
      const chosen = weightHit || hits[1];
      return { size: chosen.size, position: 0.5, borderline: true };
    }
    if (heightCm < chart[0].hMin) {
      return { size: chart[0].size, position: 0, borderline: false, outOfRange: 'below' };
    }
    return { size: chart[chart.length - 1].size, position: 1, borderline: false, outOfRange: 'above' };
  }

  function wearMonthsFromPosition(position) {
    return Math.max(2, Math.round((1 - position) * 11));
  }

  function bumpSize(size, delta) {
    const idx = SIZE_ORDER.indexOf(size);
    const next = Math.max(0, Math.min(SIZE_ORDER.length - 1, idx + delta));
    return SIZE_ORDER[next];
  }

  function pickGiftSize(age, build) {
    let size = age;
    if (build === 'tall') size = bumpSize(size, 1);
    return size;
  }

  // ------------------------------------------------------------
  // Product selection
  // ------------------------------------------------------------
  function productsForShopping(state, products, size) {
    const list = products.filter(function (p) {
      const genderOk = p.gender === state.gender || p.gender === 'unisex';
      const catOk = state.category === 'any' || p.category === state.category;
      const occOk = state.shop_occasion === 'any' || (p.occasions && p.occasions.indexOf(state.shop_occasion) >= 0);
      const sizeOk = !size || p.availableSizes.indexOf(size) >= 0;
      return genderOk && catOk && occOk && sizeOk;
    });
    list.sort(function (a, b) {
      return (b.bestSeller ? 1 : 0) - (a.bestSeller ? 1 : 0) || (b.giftScore - a.giftScore);
    });
    return list.slice(0, 6);
  }

  function productsForGift(state, products, size) {
    const list = products.filter(function (p) {
      if (state.gift_gender !== 'either') {
        if (p.gender !== state.gift_gender && p.gender !== 'unisex') return false;
      }
      if (state.gift_occasion !== 'any') {
        if (!p.occasions || p.occasions.indexOf(state.gift_occasion) < 0) return false;
      }
      if (size && p.availableSizes.indexOf(size) < 0) return false;
      return true;
    });
    list.sort(function (a, b) { return b.giftScore - a.giftScore; });
    return list.slice(0, 4);
  }

  // ------------------------------------------------------------
  // DOM helpers — no innerHTML with dynamic content
  // ------------------------------------------------------------
  function el(tag, opts, children) {
    const node = document.createElement(tag);
    if (opts) {
      if (opts.class) node.className = opts.class;
      if (opts.text != null) node.textContent = opts.text;
      if (opts.href) node.href = opts.href;
      if (opts.target) node.target = opts.target;
      if (opts.rel) node.rel = opts.rel;
      if (opts.src) node.src = opts.src;
      if (opts.alt) node.alt = opts.alt;
      if (opts.loading) node.loading = opts.loading;
      if (opts.type) node.type = opts.type;
    }
    if (children) {
      for (let i = 0; i < children.length; i++) if (children[i]) node.appendChild(children[i]);
    }
    return node;
  }

  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }

  // ------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------
  function renderShopping(root, state, products) {
    const result = findSizeByMeasurements(state.gender, state.category, state.height, state.weight);
    const months = wearMonthsFromPosition(result.position);
    const picks = productsForShopping(state, products, result.size);

    const r = root.querySelector('[data-size-finder-result]');
    clear(r);

    const ageLabel = { '2-3Y': '2–3 year old', '3-4Y': '3–4 year old', '4-5Y': '4–5 year old', '5-6Y': '5–6 year old' };

    // Head
    const countEl = el('div', { class: 'size-finder__size', text: result.size });
    countEl.appendChild(el('small', { text: 'fits ' + contextLabel(state) }));
    const head = el('div', { class: 'size-finder__result-head' }, [
      el('div', { class: 'size-finder__result-label', text: state.gender === 'girl' ? 'Recommended size for her' : 'Recommended size for him' }),
      countEl,
      buildFitBadges(result, months)
    ]);
    r.appendChild(head);

    // Rationale
    r.appendChild(el('div', { class: 'size-finder__rationale', text: shoppingRationale(state, result, months) }));

    // Section title
    const titleBits = [];
    titleBits.push(picks.length + ' ' + (picks.length === 1 ? 'pick' : 'picks'));
    if (state.shop_occasion !== 'any') titleBits.push('for ' + state.shop_occasion);
    if (state.category !== 'any') titleBits.push('· ' + state.category);
    titleBits.push('· size ' + result.size);
    r.appendChild(el('h3', { class: 'size-finder__section-title', text: titleBits.join(' ') }));

    if (!picks.length) {
      const msg = state.shop_occasion !== 'any'
        ? 'Nothing in our ' + state.shop_occasion + ' range matches right now. Try "Any" occasion to see more in size ' + result.size + '.'
        : 'No products match those filters right now.';
      r.appendChild(el('div', { class: 'size-finder__empty', text: msg }));
    } else {
      r.appendChild(buildProductGrid(picks, result.size, false));
    }
  }

  function renderGift(root, state, products) {
    const size = pickGiftSize(state.gift_age, state.gift_build);
    const picks = productsForGift(state, products, size);

    const r = root.querySelector('[data-size-finder-result]');
    clear(r);

    const ageLabel = { '2-3Y': '2–3 year old', '3-4Y': '3–4 year old', '4-5Y': '4–5 year old', '5-6Y': '5–6 year old' };

    // Head
    const sizeEl = el('div', { class: 'size-finder__size', text: size });
    sizeEl.appendChild(el('small', { text: 'for a ' + (ageLabel[state.gift_age] || state.gift_age) }));
    const head = el('div', { class: 'size-finder__result-head' }, [
      el('div', { class: 'size-finder__result-label', text: 'Safest gift size' }),
      sizeEl,
      buildGiftBadges(state)
    ]);
    r.appendChild(head);

    // Rationale
    r.appendChild(el('div', { class: 'size-finder__rationale', text: giftRationale(state, size) }));

    // Section title
    const titleBits = [];
    titleBits.push(picks.length + ' gift-worthy ' + (picks.length === 1 ? 'pick' : 'picks'));
    if (state.gift_occasion !== 'any') titleBits.push('for ' + state.gift_occasion);
    titleBits.push('· size ' + size);
    r.appendChild(el('h3', { class: 'size-finder__section-title', text: titleBits.join(' ') }));

    if (!picks.length) {
      const msg = state.gift_occasion !== 'any'
        ? 'No ' + state.gift_occasion + ' picks match this recipient. Try "Not sure" to see the full gift range.'
        : 'No products match this recipient right now.';
      r.appendChild(el('div', { class: 'size-finder__empty', text: msg }));
    } else {
      r.appendChild(buildProductGrid(picks, size, true));
    }
  }

  function contextLabel(state) {
    if (state.category === 'dress') return 'dresses';
    if (state.category === 'bottom') return 'bottoms';
    if (state.category === 'set') return 'sets';
    return state.gender === 'girl' ? 'girls tops & dresses' : 'boys shirts';
  }

  function buildFitBadges(result, months) {
    const wrap = el('div', { class: 'size-finder__badges' });
    if (result.outOfRange === 'below') {
      wrap.appendChild(el('span', { class: 'size-finder__badge is-warn', text: 'Below our smallest size — may run large' }));
    } else if (result.outOfRange === 'above') {
      wrap.appendChild(el('span', { class: 'size-finder__badge is-warn', text: 'Above our largest size — will be snug' }));
    } else if (result.borderline) {
      wrap.appendChild(el('span', { class: 'size-finder__badge is-warn', text: 'Between sizes — we picked the safer one' }));
    } else {
      wrap.appendChild(el('span', { class: 'size-finder__badge is-good', text: 'Confident fit' }));
    }
    wrap.appendChild(el('span', { class: 'size-finder__badge', text: 'Fits for approx. ' + months + ' months' }));
    return wrap;
  }

  function buildGiftBadges(state) {
    const wrap = el('div', { class: 'size-finder__badges' });
    if (state.gift_build === 'tall') wrap.appendChild(el('span', { class: 'size-finder__badge is-good', text: 'Sized up for a tall build' }));
    if (state.gift_build === 'small') wrap.appendChild(el('span', { class: 'size-finder__badge is-good', text: 'True to age · won\u2019t be too big' }));
    if (state.gift_occasion !== 'any') wrap.appendChild(el('span', { class: 'size-finder__badge', text: 'Matched to ' + state.gift_occasion }));
    wrap.appendChild(el('span', { class: 'size-finder__badge', text: 'Will fit through the season' }));
    return wrap;
  }

  function shoppingRationale(state, result, months) {
    if (result.outOfRange === 'below') {
      return 'Your kid is smaller than our smallest size (2–3Y). Consider a forgiving silhouette like a dress or a co-ord set.';
    }
    if (result.outOfRange === 'above') {
      return 'Your kid is larger than our largest size (5–6Y). A set with an elastic waistband or a looser-fitting dress may work best.';
    }
    if (result.borderline) {
      return 'At ' + state.height + ' cm and ' + state.weight + ' kg, your kid is between two of our sizes. We picked the safer one based on weight. If you want longer wear, size up.';
    }
    const pos = result.position < 0.33 ? 'just entering' : (result.position > 0.67 ? 'near the top of' : 'sitting comfortably in');
    return 'At ' + state.height + ' cm and ' + state.weight + ' kg, your kid is ' + pos + ' size ' + result.size + '. Expect comfortable wear for approximately ' + months + ' months.';
  }

  function giftRationale(state, size) {
    const ageLabel = { '2-3Y': '2–3 year old', '3-4Y': '3–4 year old', '4-5Y': '4–5 year old', '5-6Y': '5–6 year old' };
    const buildNote = state.gift_build === 'tall'
      ? "Because you mentioned they\u2019re tall for their age, we sized up one bracket so the gift fits through the coming months."
      : "We stayed true to age — big enough to be comfortable, not so big that it looks borrowed from an older sibling.";
    const occasionNote = state.gift_occasion !== 'any'
      ? ' The picks below are matched to ' + state.gift_occasion + ' wear.'
      : '';
    return 'For a ' + (ageLabel[state.gift_age] || state.gift_age) + ', size ' + size + ' is the safest gift. ' + buildNote + occasionNote;
  }

  function buildProductGrid(products, size, isGift) {
    const grid = el('div', { class: 'size-finder__cards' });
    for (let i = 0; i < products.length; i++) {
      grid.appendChild(buildCard(products[i], size, isGift));
    }
    return grid;
  }

  function buildCard(p, size, isGift) {
    // Deep-link to the specific variant for the recommended size, if we can find one
    let productUrl = p.url;
    const matchingVariant = findVariantForSize(p.variants, size);
    if (matchingVariant && matchingVariant.id) {
      const separator = productUrl.indexOf('?') >= 0 ? '&' : '?';
      productUrl = productUrl + separator + 'variant=' + matchingVariant.id;
    }

    const thumb = el('div', { class: 'size-finder__card-thumb' });
    if (p.image) {
      thumb.appendChild(el('img', { src: p.image, alt: p.title, loading: 'lazy' }));
    }
    if (p.bestSeller) thumb.appendChild(el('span', { class: 'size-finder__card-badge', text: 'Bestseller' }));
    if (isGift && p.giftScore >= 9) thumb.appendChild(el('span', { class: 'size-finder__card-gift-badge', text: 'Gift pick' }));

    const body = el('div', { class: 'size-finder__card-body' }, [
      el('div', { class: 'size-finder__card-name', text: p.title }),
      el('div', { class: 'size-finder__card-subtitle', text: p.subtitle }),
      el('div', { class: 'size-finder__card-price', text: formatPrice(p.price) }),
      el('span', { class: 'size-finder__card-size-tag', text: 'Recommended · ' + size })
    ]);

    return el('a', {
      class: 'size-finder__card',
      href: productUrl,
      rel: 'noopener'
    }, [thumb, body]);
  }

  function findVariantForSize(variants, size) {
    if (!variants || !size) return null;
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      const opt = normalizeSize(v.option1 || v.title || '');
      if (opt === size && v.available) return v;
    }
    return null;
  }

  function formatPrice(cents) {
    if (!cents) return '';
    const rupees = Math.round(cents / 100);
    return '\u20B9' + rupees.toLocaleString('en-IN');
  }

  // ------------------------------------------------------------
  // Main render dispatch
  // ------------------------------------------------------------
  function render(root, state, products) {
    if (state.mode === 'shopping') renderShopping(root, state, products);
    else renderGift(root, state, products);
  }

  // ------------------------------------------------------------
  // Wire inputs
  // ------------------------------------------------------------
  function wireInputs(root, state, products) {
    const shoppingBtn = root.querySelector('[data-mode="shopping"]');
    const giftBtn = root.querySelector('[data-mode="gift"]');
    const shoppingForm = root.querySelector('[data-form="shopping"]');
    const giftForm = root.querySelector('[data-form="gift"]');

    function setMode(mode) {
      state.mode = mode;
      if (mode === 'shopping') {
        shoppingBtn.classList.add('is-active');
        giftBtn.classList.remove('is-active');
        shoppingForm.classList.remove('size-finder__hidden');
        giftForm.classList.add('size-finder__hidden');
      } else {
        giftBtn.classList.add('is-active');
        shoppingBtn.classList.remove('is-active');
        giftForm.classList.remove('size-finder__hidden');
        shoppingForm.classList.add('size-finder__hidden');
      }
      render(root, state, products);
    }

    if (shoppingBtn) shoppingBtn.addEventListener('click', function () { setMode('shopping'); });
    if (giftBtn) giftBtn.addEventListener('click', function () { setMode('gift'); });

    // Ensure initial mode reflects state
    setMode(state.mode);

    // Option groups (pill buttons)
    const groups = root.querySelectorAll('[data-field]');
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const field = group.getAttribute('data-field');
      group.addEventListener('click', function (e) {
        const btn = e.target.closest('button');
        if (!btn) return;
        const buttons = group.querySelectorAll('button');
        for (let j = 0; j < buttons.length; j++) buttons[j].classList.remove('is-active');
        btn.classList.add('is-active');
        state[field] = btn.getAttribute('data-val');
        render(root, state, products);
      });
    }

    // Height and weight sliders
    const heightIn = root.querySelector('[data-input="height"]');
    const heightLbl = root.querySelector('[data-label="height"]');
    const weightIn = root.querySelector('[data-input="weight"]');
    const weightLbl = root.querySelector('[data-label="weight"]');

    if (heightIn && heightLbl) {
      heightIn.addEventListener('input', function () {
        state.height = Number(heightIn.value);
        heightLbl.textContent = heightIn.value;
        if (state.mode === 'shopping') render(root, state, products);
      });
    }
    if (weightIn && weightLbl) {
      weightIn.addEventListener('input', function () {
        state.weight = Number(weightIn.value);
        weightLbl.textContent = weightIn.value;
        if (state.mode === 'shopping') render(root, state, products);
      });
    }
  }

  // ------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
